import { getBreakUntilMs } from './breakSessionStorage';

/**
 * Build in-app header notifications from punchcard `/punchcard/status` payload + local break session.
 * @param {Record<string, unknown> | null} detail
 * @returns {Array<{ id: string, level: 'info' | 'success' | 'warning' | 'error', title: string, subtitle: string, path: string | null }>}
 */
export function buildHeaderNotifications(detail) {
  const items = [];

  const breakUntil = getBreakUntilMs();
  if (breakUntil != null && breakUntil > Date.now()) {
    const end = new Date(breakUntil);
    items.push({
      id: 'break',
      level: 'info',
      title: 'Break in progress',
      subtitle: `Timed break — resume work by ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
      path: null
    });
  }

  if (!detail || typeof detail !== 'object') {
    if (!items.length) {
      items.push({
        id: 'no-data',
        level: 'info',
        title: 'Status unavailable',
        subtitle: 'Could not load punch / time status yet. Try again in a moment.',
        path: '/punchcard'
      });
    }
    return items;
  }


  const idleT = Number(detail.idleTimeoutMinutes) || 10;
  const mins = Number(detail.minutesSinceLastActivity) || 0;
  const online = detail.isOnline === true;
  const idle = detail.isIdle === true;
  const sessions = Array.isArray(detail.todaySessions) ? detail.todaySessions : [];
  const hasAnyToday = sessions.length > 0;
  const activeSession = detail.currentSession != null;

  if (activeSession && online && !idle) {
    const hours = detail.totalWorkingHours != null ? String(detail.totalWorkingHours) : '0';
    const actCount = detail.todayActivityCount != null ? detail.todayActivityCount : 0;
    items.push({
      id: 'working',
      level: 'success',
      title: 'You are punched in',
      subtitle: `About ${hours} h tracked today · ${actCount} logged activities today.`,
      path: '/punchcard'
    });
    if (idleT > 0 && mins >= Math.max(0, idleT - 3)) {
      const left = Math.max(0, idleT - mins);
      items.push({
        id: 'idle-soon',
        level: 'warning',
        title: 'Idle timeout approaching',
        subtitle:
          left <= 0
            ? 'Log a quick activity from the header or My Activities so your session is not auto-closed for idle.'
            : `About ${mins} min since last engagement. You may be marked idle in ~${left} min if nothing is logged.`,
        path: '/activities'
      });
    }
  } else if (!activeSession && hasAnyToday) {
    const sub = idle
      ? 'Punch in when you resume. If a session was closed for idle, logging activities helps avoid losing tracked time.'
      : 'Punch in when you start working again, or you may be done for the day.';
    items.push({
      id: 'between-sessions',
      level: idle ? 'warning' : 'info',
      title: 'No active punch session',
      subtitle: sub,
      path: '/punchcard'
    });
  } else if (!activeSession && !hasAnyToday) {
    items.push({
      id: 'not-punched',
      level: 'info',
      title: 'Not punched in today',
      subtitle: 'Use Punch In on the clock or open Punch Card to start tracking time.',
      path: '/punchcard'
    });
  }

  if (!items.length) {
    items.push({
      id: 'all-clear',
      level: 'success',
      title: "You're all caught up",
      subtitle: 'No time-tracking alerts right now.',
      path: null
    });
  }

  return items;
}

export function headerNotificationBadgeCount(items) {
  const n = items.filter((i) => i.level === 'warning' || i.level === 'error').length;
  return n;
}
